// A15: Captain profile + score history. Spec §Scores and Memory:
// "Humans get profiles and scores too. The Captain can compare their
// orchestration style against agents, watch their own signal evolve,
// and later clone pieces of their working style into a persona."
//
// v1 ships the read surface — score timeline + per-axis aggregate.
// "Clone working style" → persona-card lands as a follow-up wired to
// Pearl's gallery.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SidebarShell } from "../layout/SidebarShell";
import {
  ApiError,
  extractRows,
  getIdentity,
  getUser,
  listUserScores,
} from "../api/client";

interface ScoreRollupRow {
  axis: string;
  sub_axis: string;
  sample_size: number;
  cumulative_delta: number;
  confidence: number;
  last_updated_at: number;
}

export function UserProfilePage() {
  const identity = getIdentity();
  const user = getUser();
  const [rows, setRows] = useState<ScoreRollupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!identity) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await listUserScores(identity);
        if (cancelled) return;
        const raw = extractRows(result);
        const parsed: ScoreRollupRow[] = raw.map((r) => ({
          axis: String(r[0] ?? ""),
          sub_axis: String(r[1] ?? ""),
          sample_size: Number(r[2] ?? 0),
          cumulative_delta: Number(r[3] ?? 0),
          confidence: Number(r[4] ?? 0),
          last_updated_at: Number(r[5] ?? 0),
        }));
        setRows(parsed);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identity]);

  if (!identity || !user) {
    return (
      <SidebarShell>
        <div className="mx-auto mt-12 max-w-md text-center text-sm text-muted">
          Sign in to view your profile.
        </div>
      </SidebarShell>
    );
  }

  return (
    <SidebarShell>
      <div className="mx-auto mt-6 flex w-full max-w-3xl flex-col gap-4 px-6 pb-12">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-lg font-semibold text-primary">
            {user.display_name || user.email}
          </h1>
          <p className="font-code text-[0.625rem] text-faint">
            {identity.slice(0, 16)}…
          </p>
        </div>
        <Link
          to="/"
          className="text-xs text-muted hover:text-accent"
        >
          ← Back
        </Link>
      </header>

      <section className="rounded-panel border border-edge bg-surface-glass p-4">
        <h2 className="text-sm font-semibold text-primary">
          Score history
        </h2>
        <p className="mt-1 text-xs text-muted">
          Cumulative deltas across every workspace you've participated
          in. Sample size grows with each gate-crossing or decision-
          weight action; confidence widens with low samples.
        </p>
        {loading ? (
          <p className="mt-3 text-[0.625rem] text-faint">Loading…</p>
        ) : error ? (
          <p className="mt-3 text-[0.625rem] text-danger">{error}</p>
        ) : rows.length === 0 ? (
          <p className="mt-3 text-[0.625rem] text-faint">
            No scorings yet — Shore hasn't graded any of your decisions.
            Resolve a needs_human_input or approve/reject a commit to
            seed the timeline.
          </p>
        ) : (
          <ul className="mt-3 flex flex-col gap-1">
            {rows.map((r, idx) => {
              const tone =
                r.cumulative_delta > 0.05
                  ? "text-success"
                  : r.cumulative_delta < -0.05
                    ? "text-danger"
                    : "text-muted";
              return (
                <li
                  key={idx}
                  className="flex items-baseline justify-between gap-2 rounded-md border border-edge bg-surface-raised px-2 py-1"
                >
                  <span className="font-code text-xs text-primary">
                    {r.axis}
                    {r.sub_axis ? ` / ${r.sub_axis}` : ""}
                  </span>
                  <span className={`font-code text-xs ${tone}`}>
                    {r.cumulative_delta > 0 ? "+" : ""}
                    {r.cumulative_delta.toFixed(2)}
                    <span className="ml-2 text-[0.625rem] text-faint">
                      n={r.sample_size} · conf={r.confidence.toFixed(2)}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      </div>
    </SidebarShell>
  );
}
